import type { RouteObject } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import { AppError } from '@dma/shared';
import { HomePage } from '../features/home';
import { ContactPage } from '../features/contact';
import { AboutPage } from '../features/about';
import { ServicesPage, ServiceDetailPage } from '../features/services';
import { ProjectsPage, ProjectDetailPage } from '../features/projects';
import { GalleryPage } from '../features/gallery';
import { MineralsPage, MineralDetailPage } from '../features/minerals';
import { LoginPage, AdminLayout, AdminDashboardPage } from '../features/admin';
import {
  ScientificEntryListPage,
  ScientificEntryDetailPage,
  AdminScientificEntryListPage,
  AdminScientificEntryFormPage,
  type ScientificEntryCategory,
  type SaveScientificEntryDto
} from '../features/scientific-content';
import {
  ArticleListPage,
  ArticleDetailPage,
  AdminArticleListPage,
  AdminArticleFormPage,
  type ArticleCategory,
  type SaveArticleDto
} from '../features/news';
import { AdminBannerPage, type SaveBannerDto } from '../features/banner';
import type { CompositionRoot } from './composition-root';

/**
 * Builds the public list+detail route pair for one scientific-content
 * category (mineralogy or petrology). Both share the exact same components
 * (see features/scientific-content/components) — only the category, URL
 * prefix and heading differ, which the loaders bake into the data they
 * return so the components stay category-agnostic.
 */
function createScientificContentPublicRoutes(
  compositionRoot: CompositionRoot,
  category: ScientificEntryCategory,
  urlSegment: string,
  heading: string
): RouteObject[] {
  const basePath = `/${urlSegment}`;

  return [
    {
      path: urlSegment,
      element: <ScientificEntryListPage />,
      loader: async () => {
        const entries = await compositionRoot.getScientificEntriesUseCase.execute(category);
        return { heading, basePath, entries };
      }
    },
    {
      path: `${urlSegment}/:slug`,
      element: <ScientificEntryDetailPage />,
      loader: async ({ params }) => {
        const entry = await compositionRoot.getScientificEntryBySlugUseCase.execute(
          category,
          params.slug ?? ''
        );
        return { heading, basePath, entry };
      }
    }
  ];
}

/**
 * Builds the public list+detail route pair for one news/articles category.
 * Same shape as createScientificContentPublicRoutes above — see that
 * function's comment for why a factory instead of two near-identical route
 * blocks.
 */
function createArticlePublicRoutes(
  compositionRoot: CompositionRoot,
  category: ArticleCategory,
  urlSegment: string,
  heading: string,
  seoTitle: string,
  seoDescription: string
): RouteObject[] {
  const basePath = `/${urlSegment}`;

  return [
    {
      path: urlSegment,
      element: <ArticleListPage />,
      loader: async () => {
        const articles = await compositionRoot.getArticlesUseCase.execute(category);
        return { heading, basePath, seoTitle, seoDescription, articles };
      }
    },
    {
      path: `${urlSegment}/:slug`,
      element: <ArticleDetailPage />,
      loader: async ({ params }) => {
        const article = await compositionRoot.getArticleBySlugUseCase.execute(category, params.slug ?? '');
        return { heading, basePath, article };
      }
    }
  ];
}

/**
 * Central route registry. Per docs/architecture/FINAL-ARCHITECTURE.md, each
 * feature owns its own route definition — this file only wires them together.
 * Routes are built from a factory (not a static array) because each route's
 * `loader`/`action` needs the already-resolved use cases from the
 * Composition Root (per docs/architecture/01-dependency-injection-strategy.md),
 * which do not exist until the app has started and the environment has
 * been validated.
 */
export function createFeatureRoutes(compositionRoot: CompositionRoot): RouteObject[] {
  return [
    {
      index: true,
      element: <HomePage />,
      loader: async () => {
        const [hero, banner, about, minerals, news, articles] = await Promise.all([
          compositionRoot.getHomeHeroContentUseCase.execute(),
          compositionRoot.getActiveBannerUseCase.execute(),
          compositionRoot.getAboutContentUseCase.execute(),
          compositionRoot.getMineralsUseCase.execute(),
          compositionRoot.getArticlesUseCase.execute('news'),
          compositionRoot.getArticlesUseCase.execute('article')
        ]);
        return {
          hero,
          banner,
          aboutIntroduction: about.introduction,
          aboutStatistics: about.statistics,
          minerals: minerals.slice(0, 3),
          news: news.slice(0, 3),
          articles: articles.slice(0, 3)
        };
      }
    },
    {
      path: 'contact',
      element: <ContactPage />,
      action: async ({ request }) => {
        const formData = await request.formData();
        const phoneValue = formData.get('phone');

        try {
          const result = await compositionRoot.submitContactMessageUseCase.execute({
            name: String(formData.get('name') ?? ''),
            email: String(formData.get('email') ?? ''),
            phone: phoneValue ? String(phoneValue) : undefined,
            subject: String(formData.get('subject') ?? ''),
            message: String(formData.get('message') ?? '')
          });
          return { ok: true as const, result };
        } catch (error) {
          // Per docs/architecture/05-error-handling-strategy.md: only the
          // AppError's user-safe message ever reaches the UI, never a raw
          // stack trace or infrastructure error.
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: 'about',
      element: <AboutPage />,
      loader: () => compositionRoot.getAboutContentUseCase.execute()
    },
    {
      path: 'services',
      element: <ServicesPage />,
      loader: () => compositionRoot.getServicesUseCase.execute()
    },
    {
      path: 'services/:slug',
      element: <ServiceDetailPage />,
      loader: ({ params }) => compositionRoot.getServiceBySlugUseCase.execute(params.slug ?? '')
    },
    {
      path: 'projects',
      element: <ProjectsPage />,
      loader: () => compositionRoot.getProjectsUseCase.execute()
    },
    {
      // Alias per Sprint 1 Part 2 TASK 2: "mining activities" is the same
      // domain as Projects (location, mineral type, status, technical info)
      // — reuses the exact same element and use case, not a parallel module.
      path: 'mines',
      element: <ProjectsPage />,
      loader: () => compositionRoot.getProjectsUseCase.execute()
    },
    {
      path: 'projects/:slug',
      element: <ProjectDetailPage />,
      loader: ({ params }) => compositionRoot.getProjectBySlugUseCase.execute(params.slug ?? '')
    },
    {
      path: 'mines/:slug',
      element: <ProjectDetailPage />,
      loader: ({ params }) => compositionRoot.getProjectBySlugUseCase.execute(params.slug ?? '')
    },
    {
      path: 'gallery',
      element: <GalleryPage />,
      loader: () => compositionRoot.getGalleryUseCase.execute()
    },
    {
      path: 'minerals',
      element: <MineralsPage />,
      loader: () => compositionRoot.getMineralsUseCase.execute()
    },
    {
      path: 'minerals/:slug',
      element: <MineralDetailPage />,
      loader: ({ params }) => compositionRoot.getMineralBySlugUseCase.execute(params.slug ?? '')
    },
    ...createScientificContentPublicRoutes(compositionRoot, 'mineral', 'mineralogy', 'کانی‌شناسی'),
    ...createScientificContentPublicRoutes(compositionRoot, 'rock', 'petrology', 'سنگ‌شناسی'),
    ...createArticlePublicRoutes(
      compositionRoot,
      'news',
      'news',
      'اخبار',
      'اخبار داریوش معدن آذر',
      'آخرین اخبار و رویدادهای داریوش معدن آذر.'
    ),
    ...createArticlePublicRoutes(
      compositionRoot,
      'article',
      'articles',
      'مقالات',
      'مقالات داریوش معدن آذر',
      'مقالات تخصصی داریوش معدن آذر در حوزه معدن و صنایع معدنی.'
    )
  ];
}

/**
 * Resolves the current admin's AuthorizedUser for permission-gated use cases
 * (GetAdminScientificEntriesUseCase, SaveScientificEntryUseCase, etc). Called
 * again here rather than trusting a cached value — loaders run independently
 * of the parent `admin-root` loader that already redirected unauthenticated
 * visitors away, so this is defense in depth, not the primary guard.
 */
async function getActingUser(compositionRoot: CompositionRoot) {
  const session = await compositionRoot.getCurrentSessionUseCase.execute();
  return session?.user ?? null;
}

/**
 * Builds the admin list/create/edit/delete route set for one scientific-content
 * category. Meant to be spread into `admin-root`'s `children` — see
 * createAdminRoutes below — so every route here inherits that parent's
 * session guard and dark AdminLayout chrome automatically.
 */
function createScientificContentAdminRoutes(
  compositionRoot: CompositionRoot,
  category: ScientificEntryCategory,
  urlSegment: string,
  heading: string
): RouteObject[] {
  const basePath = `/admin/${urlSegment}`;

  return [
    {
      path: urlSegment,
      element: <AdminScientificEntryListPage />,
      loader: async () => {
        const actingUser = await getActingUser(compositionRoot);
        const entries = await compositionRoot.getAdminScientificEntriesUseCase.execute(actingUser, category);
        return { heading, basePath, entries };
      }
    },
    {
      path: `${urlSegment}/new`,
      element: <AdminScientificEntryFormPage />,
      loader: () => ({ heading: `${heading} — مطلب جدید`, basePath, category, entry: null }),
      action: async ({ request }) => {
        const actingUser = await getActingUser(compositionRoot);
        const payload = (await request.json()) as SaveScientificEntryDto;
        try {
          const entry = await compositionRoot.saveScientificEntryUseCase.execute(actingUser, {
            ...payload,
            id: null,
            category
          });
          return redirect(`${basePath}/${entry.id}/edit`);
        } catch (error) {
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: `${urlSegment}/:id/edit`,
      element: <AdminScientificEntryFormPage />,
      loader: async ({ params }) => {
        const actingUser = await getActingUser(compositionRoot);
        const entry = await compositionRoot.getScientificEntryByIdUseCase.execute(actingUser, params.id ?? '');
        return { heading: `${heading} — ویرایش`, basePath, category, entry };
      },
      action: async ({ request, params }) => {
        const actingUser = await getActingUser(compositionRoot);
        const payload = (await request.json()) as SaveScientificEntryDto;
        try {
          await compositionRoot.saveScientificEntryUseCase.execute(actingUser, {
            ...payload,
            id: params.id ?? null,
            category
          });
          return redirect(basePath);
        } catch (error) {
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: `${urlSegment}/:id/delete`,
      action: async ({ params }) => {
        const actingUser = await getActingUser(compositionRoot);
        await compositionRoot.deleteScientificEntryUseCase.execute(actingUser, params.id ?? '');
        return redirect(basePath);
      }
    }
  ];
}

/**
 * Builds the admin list/create/edit/delete route set for one news/articles
 * category. Same shape as createScientificContentAdminRoutes above, except
 * SaveArticleUseCase checks granular news:create/news:update/news:publish
 * permissions itself (see that use case) rather than one catch-all
 * permission — this factory doesn't need to know that, it just calls it.
 */
function createArticleAdminRoutes(
  compositionRoot: CompositionRoot,
  category: ArticleCategory,
  urlSegment: string,
  heading: string
): RouteObject[] {
  const basePath = `/admin/${urlSegment}`;

  return [
    {
      path: urlSegment,
      element: <AdminArticleListPage />,
      loader: async () => {
        const actingUser = await getActingUser(compositionRoot);
        const articles = await compositionRoot.getAdminArticlesUseCase.execute(actingUser, category);
        return { heading, basePath, articles };
      }
    },
    {
      path: `${urlSegment}/new`,
      element: <AdminArticleFormPage />,
      loader: () => ({ heading: `${heading} — مطلب جدید`, basePath, category, article: null }),
      action: async ({ request }) => {
        const actingUser = await getActingUser(compositionRoot);
        const payload = (await request.json()) as SaveArticleDto;
        try {
          const article = await compositionRoot.saveArticleUseCase.execute(actingUser, {
            ...payload,
            id: null,
            category
          });
          return redirect(`${basePath}/${article.id}/edit`);
        } catch (error) {
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: `${urlSegment}/:id/edit`,
      element: <AdminArticleFormPage />,
      loader: async ({ params }) => {
        const actingUser = await getActingUser(compositionRoot);
        const article = await compositionRoot.getArticleByIdUseCase.execute(actingUser, params.id ?? '');
        return { heading: `${heading} — ویرایش`, basePath, category, article };
      },
      action: async ({ request, params }) => {
        const actingUser = await getActingUser(compositionRoot);
        const payload = (await request.json()) as SaveArticleDto;
        try {
          await compositionRoot.saveArticleUseCase.execute(actingUser, {
            ...payload,
            id: params.id ?? null,
            category
          });
          return redirect(basePath);
        } catch (error) {
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: `${urlSegment}/:id/delete`,
      action: async ({ params }) => {
        const actingUser = await getActingUser(compositionRoot);
        await compositionRoot.deleteArticleUseCase.execute(actingUser, params.id ?? '');
        return redirect(basePath);
      }
    }
  ];
}

/**
 * Admin routes, kept separate from `createFeatureRoutes` because they must
 * NOT render inside the public `PageShell` (no site Header/Footer) — see
 * app/router.tsx, where these are mounted as their own top-level branch.
 */
export function createAdminRoutes(compositionRoot: CompositionRoot): RouteObject[] {
  return [
    {
      path: 'admin/login',
      element: <LoginPage />,
      action: async ({ request }) => {
        const formData = await request.formData();
        try {
          await compositionRoot.signInUseCase.execute({
            email: String(formData.get('email') ?? ''),
            password: String(formData.get('password') ?? '')
          });
          return redirect('/admin');
        } catch (error) {
          if (error instanceof AppError) {
            return { ok: false as const, message: error.message };
          }
          throw error;
        }
      }
    },
    {
      path: 'admin/logout',
      action: async () => {
        await compositionRoot.signOutUseCase.execute();
        return redirect('/admin/login');
      }
    },
    {
      id: 'admin-root',
      path: 'admin',
      element: <AdminLayout />,
      // Guard: every /admin/* page depends on this loader having run first.
      // No session → redirect before any protected UI or data ever renders.
      loader: async () => {
        const session = await compositionRoot.getCurrentSessionUseCase.execute();
        if (!session) {
          throw redirect('/admin/login');
        }
        return session;
      },
      children: [
        {
          index: true,
          element: <AdminDashboardPage />
        },
        {
          // Action-only route (no element/loader) — consumed by
          // ImageUploadField's useFetcher across every admin form that
          // embeds it (banner, article, scientific-entry). Living under
          // admin-root means it inherits that route's session guard for
          // free; UploadImageUseCase itself re-checks permission anyway
          // (see that use case's comment for why).
          path: 'media/upload',
          action: async ({ request }) => {
            const actingUser = await getActingUser(compositionRoot);
            const formData = await request.formData();
            const file = formData.get('file');
            const folder = String(formData.get('folder') ?? 'uploads');

            if (!(file instanceof File)) {
              return { ok: false as const, message: 'فایلی برای آپلود انتخاب نشده است.' };
            }

            try {
              const url = await compositionRoot.uploadImageUseCase.execute(actingUser, file, folder);
              return { ok: true as const, url };
            } catch (error) {
              if (error instanceof AppError) {
                return { ok: false as const, message: error.message };
              }
              throw error;
            }
          }
        },
        {
          path: 'banner',
          element: <AdminBannerPage />,
          loader: async () => {
            const actingUser = await getActingUser(compositionRoot);
            const banner = await compositionRoot.getBannerForAdminUseCase.execute(actingUser);
            return { banner };
          },
          action: async ({ request }) => {
            const actingUser = await getActingUser(compositionRoot);
            const payload = (await request.json()) as SaveBannerDto;
            try {
              await compositionRoot.saveBannerUseCase.execute(actingUser, payload);
              return redirect('/admin/banner');
            } catch (error) {
              if (error instanceof AppError) {
                return { ok: false as const, message: error.message };
              }
              throw error;
            }
          }
        },
        ...createScientificContentAdminRoutes(compositionRoot, 'mineral', 'mineralogy', 'کانی‌شناسی'),
        ...createScientificContentAdminRoutes(compositionRoot, 'rock', 'petrology', 'سنگ‌شناسی'),
        ...createArticleAdminRoutes(compositionRoot, 'news', 'news', 'اخبار'),
        ...createArticleAdminRoutes(compositionRoot, 'article', 'articles', 'مقالات')
      ]
    }
  ];
}
